import { useCallback, useMemo, useState } from "react";

export const useGalleryFromUrlSelection = ({
  galleryDisplayData,
  multiple,
  maxSelections = null,
}) => {
  const usePickerMode = !multiple;
  const galleryMultiple = usePickerMode ? true : multiple;

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedPickerIndex, setSelectedPickerIndex] = useState(null);

  const resetSelection = useCallback(() => {
    setSelectedItems([]);
    setSelectedPickerIndex(null);
  }, []);

  const onSelectItem = useCallback(
    (index) => {
      const item = galleryDisplayData[index];
      if (!item?.id) return;

      if (usePickerMode) {
        const isDeselect = selectedPickerIndex === index;
        setSelectedPickerIndex(isDeselect ? null : index);
        setSelectedItems(isDeselect ? [] : [item.id]);
        return;
      }

      const itemId = item.id;

      setSelectedItems((prevSelected) => {
        if (prevSelected.includes(itemId)) {
          return prevSelected.filter((id) => id !== itemId);
        }
        if (multiple) {
          if (maxSelections && prevSelected.length >= maxSelections) {
            return prevSelected;
          }
          return [...prevSelected, itemId];
        }
        return [itemId];
      });
    },
    [
      galleryDisplayData,
      maxSelections,
      multiple,
      usePickerMode,
      selectedPickerIndex,
    ]
  );

  const selectedGalleryIndexes = useMemo(() => {
    if (usePickerMode) {
      return selectedPickerIndex !== null ? [selectedPickerIndex] : [];
    }
    return galleryDisplayData
      .map((item, index) =>
        item?.id && selectedItems.includes(item.id) ? index : null
      )
      .filter((index) => index !== null);
  }, [galleryDisplayData, selectedItems, usePickerMode, selectedPickerIndex]);

  const hasSelection = usePickerMode
    ? selectedPickerIndex !== null
    : selectedItems.length > 0;

  const getSelectedItems = useCallback(
    (indexes) => {
      if (indexes) {
        return indexes
          .map((index) => galleryDisplayData[index])
          .filter(Boolean);
      }
      if (usePickerMode && selectedPickerIndex !== null) {
        return [galleryDisplayData[selectedPickerIndex]].filter(Boolean);
      }
      return selectedItems
        .map((id) => galleryDisplayData.find((item) => item.id === id))
        .filter(Boolean);
    },
    [galleryDisplayData, selectedItems, usePickerMode, selectedPickerIndex]
  );

  return {
    usePickerMode,
    galleryMultiple,
    selectedItems,
    setSelectedItems,
    selectedPickerIndex,
    setSelectedPickerIndex,
    onSelectItem,
    selectedGalleryIndexes,
    hasSelection,
    resetSelection,
    getSelectedItems,
  };
};
